// src/app/navigation/nav-model.ts

/** Which user type sees an item. 'all' = everyone, 'internal' = internal staff only. */
export type UserScope = 'all' | 'internal';

export interface NavItem {
  /** Stable identifier, also used by the bottom-bar selection. */
  id: string;
  /** i18n translation key for the label. */
  labelKey: string;
  /** Router link (unchanged from current routes). */
  route: string;
  /** Font Awesome icon class, rendered in FGTB red. */
  icon: string;
  /** Short i18n key used in the mobile bottom bar (falls back to labelKey). */
  shortLabelKey?: string;
  /** Default 'all'. */
  scope?: UserScope;
}

export interface NavGroup {
  id: string;
  /** Group header i18n key. Undefined = ungrouped (no header). */
  labelKey?: string;
  items: NavItem[];
}

/** Single source of truth for the whole app navigation. Routes verified against app-routing.module.ts. */
export const MYABVV_NAV: NavGroup[] = [
  // Note: "Chercher un membre" (internal-only) is NOT in the sidebar — it lives in the
  // header actions zone (see MenuComponent), as it is an action rather than a nav rubric.
  {
    id: 'home',
    items: [
      {
        id: 'recent-infos', labelKey: 'MENU.recent_infos', shortLabelKey: 'MENU.bottom_home',
        route: '/unemployment/recent-infos', icon: 'fa-solid fa-house'
      },
    ],
  },
  {
    id: 'unemployment',
    labelKey: 'MENU.group_unemployment',
    items: [
      {
        id: 'payments', labelKey: 'MENU.payments-overview', shortLabelKey: 'MENU.bottom_payments',
        route: '/unemployment/payments', icon: 'fa-solid fa-euro-sign'
      },
      { id: 'requests', labelKey: 'MENU.requests-overview', route: '/unemployment/requests', icon: 'fa-solid fa-clipboard-list' },
      { id: 'e-docs', labelKey: 'MENU.e-docs', route: '/unemployment/e-docs', icon: 'fa-solid fa-id-card' },
      { id: 'vacation', labelKey: 'MENU.vacation', route: '/unemployment/vacation', icon: 'fa-solid fa-umbrella-beach' },
      { id: 'permits', labelKey: 'MENU.permits', route: '/unemployment/permits', icon: 'fa-solid fa-earth-europe' },
    ],
  },
  {
    id: 'documents',
    labelKey: 'MENU.documents',
    items: [
      { id: 'attestations', labelKey: 'MENU.attestations', route: '/unemployment/attestations', icon: 'fa-solid fa-certificate' },
      { id: 'tax-forms', labelKey: 'MENU.tax-forms', route: '/unemployment/tax-forms', icon: 'fa-solid fa-file-lines' },
      {
        id: 'inbox', labelKey: 'MENU.inbox', shortLabelKey: 'MENU.bottom_inbox',
        route: '/unemployment/inbox', icon: 'fa-solid fa-envelope'
      },
    ],
  },
  {
    id: 'membership',
    labelKey: 'MENU.group_membership',
    items: [
      { id: 'personal-data', labelKey: 'MENU.personal-data', route: '/membership/personal-data', icon: 'fa-solid fa-user' },
      { id: 'contributions', labelKey: 'MENU.contributions', route: '/membership/contributions', icon: 'fa-solid fa-credit-card' },
    ],
  },
  {
    id: 'help',
    items: [
      { id: 'ask-question', labelKey: 'MENU.ask_question', route: '/ask-question', icon: 'fa-solid fa-circle-question' },
    ],
  },
];

/** Item ids shown in the mobile bottom bar, in order. The 4th tab ("Plus") is a fixed
 *  entry handled in the bottom-bar component (it opens the full menu grid), not a nav item. */
export const BOTTOM_BAR_ITEM_IDS = ['recent-infos', 'payments', 'inbox'] as const;
